// @flow
import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {Col, Skeleton, Row, Table, Descriptions, Tag} from "antd";
import {EditOutlined} from "@ant-design/icons";
import moment from "moment";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {companyDetailPropType, companyType} from "./onboardingCompanyDetailPropTypes";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {Img, MarkerImg, OrderMergeContainer} from "./onboardingCompanyDetailStyles";
import {courierTypes} from "../../../constants/constants";
import placeholder from "../../../public/images/company-placeholder.jpg";
import {useTranslation} from "react-i18next";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const OnboardingCompanyDetail = ({companyId}: companyDetailPropType) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation([
        "companyDetail",
        "courierTypes",
        "companyUserSelect",
        "newCompany"
    ]);
    const [activity, setActivity] = useState("all");
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [companyRecall, setCompanyRecall] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [searchCityText, setSearchCityText] = useState("all");

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId: stores.meStore.companies[0]._id,
            withSubCompanies: true
        }),
        condition: !!stores.meStore.companies[0]._id,
        dependencies: [companyRecall, activity, stores.meStore.companies[0]._id]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.meStore?.companies[0]?._id
            ? stores.companyStore.getSubCompanyList.bind(null, {
                  companyId: stores.meStore.companies[0]._id,
                  limit: 10,
                  skip: (currentPage - 1) * 10,
                  activity,
                  searchText: searchText.toLocaleLowerCase("tr-TR"),
                  city: searchCityText.toLocaleLowerCase("tr-TR")
              })
            : () => [],
        dependencies: [
            currentPage,
            activity,
            companyData,
            subCompanyRecall,
            searchText,
            searchCityText
        ]
    });

    useEffect(() => {
        if (companyData.status && companyData.status !== 200) {
            stores.appStore.openErrorModal({
                content: t("other.companyNotFound", {ns: "common"}),
                onOk: () => {
                    history.push("/companies");
                }
            });
        }
        if (companyData.status && companyData.data.deliveryList) {
            const couriers = companyData.data.deliveryList
                .filter(delivery => delivery.deliveryState !== "unassigned")
                .map(delivery => delivery.assignedCourier);
            setCourierList(couriers);
        }
    }, [companyData]);

    return useObserver(() =>
        !companyData.data ? (
            <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
        ) : (
            <>
                <Row gutter={[0, 20]} data-testid="companyDetailScreen">
                    <Col span={24}>
                        <Descriptions
                            bordered
                            column={2}
                            title={
                                <>
                                    <Row type="flex" justify="space-between">
                                        <span data-testid="companyName">
                                            {companyData.data.companyName}
                                        </span>
                                    </Row>
                                    <Row>
                                        <Img
                                            src={companyData.data.photo || placeholder}
                                            data-testid="companyImage"
                                        />
                                    </Row>
                                </>
                            }
                        >
                            <Descriptions.Item
                                span={1}
                                label={t("fields.companyActivity", {ns: "common"})}
                            >
                                <span data-testid="companyActivity">
                                    {companyData.data.disabled
                                        ? t("other.inactive", {ns: "common"})
                                        : t("other.active", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={1}
                                label={t("fields.companyCode", {ns: "common"})}
                            >
                                <span data-testid="companyCode">
                                    {companyData.data.companyCode || "-"}
                                </span>
                            </Descriptions.Item>

                            <Descriptions.Item label={t("fields.createdDate", {ns: "common"})}>
                                <span data-testid="createdAt">
                                    {moment(companyData.data.createdAt).format("ll")}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.city", {ns: "common"})}>
                                <span data-testid="city">
                                    {companyData.data.city?.capitalize()}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.county", {ns: "common"})}>
                                <span data-testid="county">
                                    {companyData.data.county?.capitalize()}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item span={2} label={t("fields.address", {ns: "common"})}>
                                <span data-testid=""></span>
                                {companyData.data.address}
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={2}
                                label={t("fields.taxOffice", {ns: "common"})}
                            >
                                <span data-testid="registeredTaxOffice">
                                    {companyData.data.registeredTaxOffice}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={2}
                                label={t("fields.taxNumber", {ns: "common"})}
                            >
                                <span data-testid="taxNumber">{companyData.data.taxNumber}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.bankName", {ns: "common"})}>
                                <span data-testid="bankName">{companyData.data.bankName}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="IBAN">
                                <span data-testid="iban">{companyData.data.iban}</span>
                            </Descriptions.Item>
                            <Descriptions.Item
                                span={2}
                                label={t("fields.billInformation", {
                                    ns: "common"
                                })}
                            >
                                <span data-testid="billInformation">
                                    {companyData.data.billInformation}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.courierTypes", {ns: "common"})}>
                                <span data-testid="courierTypes">
                                    {companyData.data.courierTypes &&
                                        courierTypes
                                            .filter(type =>
                                                companyData.data.courierTypes.includes(type.value)
                                            )
                                            .map(ty => (
                                                <Tag key={ty.value}>
                                                    {t(ty.translation_key, {ns: "courierTypes"})}
                                                </Tag>
                                            ))}
                                </span>
                            </Descriptions.Item>
                            {companyData.data.markerImage && (
                                <Descriptions.Item
                                    label={t("fields.companyMarkerImage", {ns: "common"})}
                                >
                                    <MarkerImg
                                        src={companyData.data.markerImageSrc}
                                        data-testid="markerImage"
                                    />
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        {stores.meStore.role == "admin" &&
                            companyData.data?.options?.merge?.map((mergeOptions, i) =>
                                mergeOptions.isActive ? (
                                    <OrderMergeContainer key={i} size="small" bordered>
                                        <Descriptions.Item
                                            span={3}
                                            label={t("fields.courierType", {ns: "common"})}
                                        >
                                            <span data-testid="courierType">
                                                {mergeOptions?.courierType === "motorCourier"
                                                    ? t("longForm.motorCourier", {
                                                          ns: "courierTypes"
                                                      })
                                                    : t("longForm.carCourier", {
                                                          ns: "courierTypes"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.orderMerge.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="orderMergeable">
                                                {mergeOptions?.isActive
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.clickToDoorTime.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="clickToDoorTime">
                                                {mergeOptions?.clickToDoorTime +
                                                    " " +
                                                    t("units.minute.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.prepTime.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="prepTime">
                                                {mergeOptions?.prepTime +
                                                    " " +
                                                    t("units.minute.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.prepTimeBufferPercent.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="prepTimeBufferPercent">
                                                {"%" + mergeOptions?.prepTimeBufferPercent}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.handOverTime.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="handOverTime">
                                                {mergeOptions?.handOverTime +
                                                    " " +
                                                    t("units.minute.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.mergeMaxOrder.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="mergeMaxOrder">
                                                {mergeOptions?.maxOrder +
                                                    " " +
                                                    t("units.piece.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.mergeWithOtherVehicleTypes.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="mergeWithOtherVehicleTypes">
                                                {mergeOptions?.mergeWithOtherTypes
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t(
                                                "fields.mergeOptions.mergeWithOtherCompanies.label",
                                                {ns: "common"}
                                            )}
                                        >
                                            <span data-testid="mergeWithOtherCompanies">
                                                {mergeOptions?.mergeWithOtherCompanies
                                                    ? t("other.active", {ns: "common"})
                                                    : t("other.passive", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.mergeOptions.maxPickup.label", {
                                                ns: "common"
                                            })}
                                        >
                                            <span data-testid="maxPickup">
                                                {mergeOptions?.maxPickup +
                                                    " " +
                                                    t("units.piece.long", {ns: "common"})}
                                            </span>
                                        </Descriptions.Item>
                                    </OrderMergeContainer>
                                ) : null
                            )}
                        {stores.meStore.role == "admin" &&
                            companyData.data?.options?.queue?.isActive && (
                                <OrderMergeContainer size="small" bordered>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.orderQueue.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="orderQueueable">
                                            {companyData.data?.options?.queue?.isActive
                                                ? t("other.active", {ns: "common"})
                                                : t("other.passive", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.maxOrderCount.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="maxOrderCount">
                                            {companyData.data?.options?.queue?.maxCount}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.maxWaitingTime.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="maxWaitingTime">
                                            {companyData.data?.options?.queue?.maxWaitingTime +
                                                " " +
                                                t("units.minute.long", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.thresholdCount.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="thresholdCount">
                                            {"%" + companyData.data?.options?.queue?.thresholdCount}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        label={t("fields.queueOptions.thresholdTime.label", {
                                            ns: "common"
                                        })}
                                    >
                                        <span data-testid="thresholdTime">
                                            {companyData.data?.options?.queue?.thresholdTime +
                                                " " +
                                                t("units.minute.long", {ns: "common"})}
                                        </span>
                                    </Descriptions.Item>
                                </OrderMergeContainer>
                            )}
                    </Col>

                    <Col span={24} data-testid="subCompanyCard">
                        <Row justify={"space-between"}></Row>
                        <Table
                            rowKey="_id"
                            data-testid="subCompanyTable"
                            dataSource={subCompanyData && subCompanyData.rows}
                            loading={subCompanyLoading}
                            style={{marginTop: 20}}
                            pagination={{
                                total: subCompanyData.dataCount,
                                current: currentPage,
                                onChange: async page => {
                                    setCurrentPage(page);
                                },
                                hideOnSinglePage: true,
                                showSizeChanger: false
                            }}
                            locale={{
                                emptyText: <EmptyMessage />
                            }}
                            bordered
                        >
                            <Column
                                title={t("fields.companyName", {ns: "common"})}
                                dataIndex="name"
                            />
                            <Column
                                title={t("fields.city", {ns: "common"})}
                                dataIndex="city"
                                render={text => (
                                    <span data-testid="city">{text?.capitalize()}</span>
                                )}
                            />
                            <Column
                                title={t("fields.county", {ns: "common"})}
                                dataIndex="county"
                                render={text => (
                                    <span data-testid="county">{text?.capitalize()}</span>
                                )}
                            />
                            <Column
                                title={t("companyDetail.fields.district", {ns: "companyDetail"})}
                                dataIndex="district"
                                render={text => (
                                    <span data-testid="district">{text?.capitalize()}</span>
                                )}
                            />
                            <Column
                                title={t("fields.address", {ns: "common"})}
                                dataIndex="address"
                                render={text => <span data-testid="address">{text}</span>}
                            />
                            <Column
                                title={t("fields.credit", {ns: "common"})}
                                dataIndex="credit"
                                render={text => <span data-testid="credit">{text ? text : 0}</span>}
                            />
                        </Table>
                    </Col>
                </Row>
            </>
        )
    );
};
